import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  IconButton,
  Grid,
  Tooltip,
  IconButtonProps,
  TooltipProps,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { DeleteOutlineOutlined, CloseOutlined } from '@mui/icons-material';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { APP_SETTINGS } from 'config/constants';
import { useTranslate } from '@Common/hooks';
import { RootState } from '../models/redux/store';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'inherit',
      color: theme.palette.primary.dark,
      cursor: 'pointer',
    },
    padding: '3px 3px 5px 5px',
  },
  dontShowLabel: {
    fontSize: '13px',
  },
  labelRoot: {
    marginLeft: 5,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    [theme.breakpoints.down(360)]: {
      marginRight: 0,
    },
  },
  dialogPaper: {
    maxWidth: '390px',
    margin: '-100px 32px 32px',
    [theme.breakpoints.down(600)]: {
      margin: '32px',
    },
    [theme.breakpoints.down(320)]: {
      marginLeft: 20,
      marginRight: 20,
    },
  },
  dialogContentText: {
    [theme.breakpoints.down(360)]: {
      fontSize: '14px',
    },
    [theme.breakpoints.down(320)]: {
      marginBottom: 0,
    },
  },
  checkBoxLabel: {
    [theme.breakpoints.down(360)]: {
      fontSize: '12px',
    },
  },
  checked: {
    color: theme.specialColors.BLUE_COLOR,
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },

  continueButton: {
    fontWeight: 600,
    minWidth: 80,
    color: theme.specialColors.BLUE_COLOR,
    position: 'relative',
    left: '-5px',
    [theme.breakpoints.down(360)]: {
      minWidth: 70,
      fontSize: 12,
    },
  },
  actionRoot: {
    [theme.breakpoints.down(360)]: {
      paddingTop: 0,
    },
  },

  checkBoxIcon: {
    padding: '6px',
    [theme.breakpoints.down(360)]: {
      paddingRight: 0,
    },
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },
  dialogContentRoot: {
    [theme.breakpoints.down(320)]: {
      marginTop: 0,
      marginBottom: 0,
    },
  },
}));

interface IProps {
  isQuilling: boolean;
  clearInputText: Function;
  style?: {};
  iconButtonClass?: string;
  iconButtonProps?: IconButtonProps;
  deleteIcon?: JSX.Element;
  tooltipPlacement?: TooltipProps['placement'];
  // opacity?: number;
}

type Products =
  | '/co-writer'
  | '/grammar-check'
  | '/summarize'
  | '/'
  | '/citation-generator'
  | '/word-counter';

type ProductLS = Record<Products, string>;

function DeleteInputText(props: IProps) {
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension);
  const isKeyboardOpen = useSelector((state: RootState) => state.common.isKeyboardOpen);

  const classes = useStyles();
  const router = useRouter();
  const LocalStorage = QuillLocalStorage.getInstance();
  const { translate } = useTranslate();

  const pathname = router.pathname as Products;
  const productLSMap: ProductLS = {
    '/': 'DONT_SHOW_DELETE_REMINDER_AGAIN',
    '/summarize': 'SUMMARIZER/DONT_SHOW_DELETE_REMINDER_AGAIN',
    '/co-writer': 'CO_WRITER/DONT_SHOW_DELETE_REMINDER_AGAIN',
    '/grammar-check': 'GRAMMAR_CHECKER/DONT_SHOW_DELETE_REMINDER_AGAIN',
    '/citation-generator': 'CITATION_GENERATOR/DONT_SHOW_DELETE_REMINDER_AGAIN',
    '/word-counter': 'WORD_COUNTER/DONT_SHOW_DELETE_REMINDER_AGAIN',
  };
  const lsName = productLSMap[pathname];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dontShowAgain, setdontShowAgain] = useState(LocalStorage.get(lsName) === 'true');

  const handleClickOpen = (event: any) => {
    event.stopPropagation();
    if (props.isQuilling) {
      return;
    }
    // @TODO: cookies not working fine right now in extension;
    // need to show dialog box in future
    if (!dontShowAgain && !isExtension) {
      setDialogOpen(true);
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
      props.clearInputText();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isKeyboardOpen) {
      handleClickOpen(e);
    }
  };

  const dontShowAgainHandler = () => {
    const newState = !dontShowAgain;
    LocalStorage.set(lsName, `${newState}`);
    setdontShowAgain(newState);
  };
  const clearSummaryData = () => {
    props.clearInputText();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    setDialogOpen(false);
  };
  const btnStyle = {
    opacity: 1,
    cursor: 'pointer',
  };
  if (props.isQuilling) {
    btnStyle.opacity = 0.6;
    btnStyle.cursor = 'pointer';
  }

  const getModalText = () => {
    const productAndTextMapping = {
      '/co-writer': 'the complete Article text',
      '/grammar-check': 'all of the text',
      '/summarize': 'the Original and Summarized text',
      '/': 'the Original and Paraphrased text',
      '/citation-generator': 'all the citations in the list',
      '/word-counter': 'all of the text',
    };
    return productAndTextMapping[pathname];
  };

  return (
    <>
      <Tooltip
        arrow
        placement={props.tooltipPlacement || 'bottom'}
        enterDelay={1000}
        title={translate(
          APP_SETTINGS.showToolTips
            ? `Delete All ${pathname === '/citation-generator' ? 'citations' : 'text'}`
            : ''
        )}
      >
        <IconButton
          className={props.iconButtonClass}
          disabled={props.isQuilling}
          classes={{ root: classes.iconButton }}
          onClick={handleClickOpen}
          // @TODO tempary fix need to handle by onClick event
          onMouseDown={handleMouseDown}
          style={{ ...props.style, ...btnStyle }}
          {...props.iconButtonProps}
          size="large"
        >
          {props.deleteIcon || <DeleteOutlineOutlined style={{ fontSize: '26px' }} />}
        </IconButton>
      </Tooltip>
      <Dialog
        classes={{ paperWidthSm: classes.dialogPaper }}
        open={dialogOpen}
        style={{ display: 'relative' }}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          style={{ position: 'absolute', top: 0, right: 0 }}
          onClick={() => setDialogOpen(false)}
          size="large"
        >
          <CloseOutlined />
        </IconButton>
        <DialogTitle
          id="delete-input-text"
          style={{ fontWeight: 600, fontSize: '20px' }}
          // disableTypography
        >
          {translate('Delete Text')}
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContentRoot }}>
          <DialogContentText id="alert-dialog-description" className={classes.dialogContentText}>
            {translate(`You’re about to delete ${getModalText()}.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions classes={{ root: classes.actionRoot }}>
          <Grid container style={{ paddingLeft: '1px' }}>
            <FormControlLabel
              classes={{
                root: classes.labelRoot,
                label: classes.checkBoxLabel,
              }}
              control={
                <Checkbox
                  color="default"
                  checked={dontShowAgain}
                  onChange={dontShowAgainHandler}
                  value="dontShowAgain"
                  className={classes.checkBoxIcon}
                  classes={{ checked: classes.checked }}
                />
              }
              label={translate("Don't show again")}
            />
          </Grid>
          <Button onClick={clearSummaryData} className={classes.continueButton} autoFocus>
            {translate('CONTINUE')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default DeleteInputText;
